import styled from "styled-components"

import { Banner, spacing, Text } from "@minuthq/meatball-ui-react"

import { EmailModeSelect } from "src/components/Account/BillingPortal/NotificationSettings/EmailModeSelect"
import { PushModeSelect } from "src/components/Account/BillingPortal/NotificationSettings/PushModeSelect"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useFetchUserNotificationSettings } from "src/data/user/queries/userQueries"
import { TUserEventNotification } from "src/data/user/types/userTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { GridTable } from "src/ui/GridTable/GridTable"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import InfoIcon from "src/ui/icons/info-i.svg"
import { MTooltip } from "src/ui/MTooltip/MTooltip"

export function EventNotificationsSettings() {
  const { t, langKeys, tPlaceholder } = useTranslate()
  const user = useGetUser()

  const fetchUserNotificationSettings = useFetchUserNotificationSettings({
    userId: user.user_id,
  })
  const alertNotifications = fetchUserNotificationSettings.data?.events.filter(
    (event) => event.category === "sensor_alert"
  )

  const tableColumns: TableColumn<TUserEventNotification>[] = [
    {
      value: "label",
      label: tPlaceholder("Event"),
      renderCell: (notification) => (
        <Text variant="subtitleS">{notification.label}</Text>
      ),
      columnWidth: "auto",
    },
    {
      value: "push",
      label: tPlaceholder("App notifications"),
      renderLabel: (label) => (
        <LabelWithTooltip
          label={label}
          tooltipText={tPlaceholder(
            "Push notifications keep you updated. Critical notification alert you right away if something needs your attention, even when your phone’s on silent."
          )}
        />
      ),
      renderCell: (notification) => {
        const channel = notification.channels.find(
          (channel) => channel.type === "push"
        )

        if (!channel) {
          return null
        }

        return (
          <div>
            <PushModeSelect notification={notification} channel={channel} />
          </div>
        )
      },
      columnWidth: "auto",
    },
    {
      value: "email",
      label: tPlaceholder("Email"),
      renderCell: (notification) => {
        const channel = notification.channels.find(
          (notification) => notification.type === "email"
        )

        if (!channel) {
          return null
        }

        return (
          <div>
            <EmailModeSelect notification={notification} channel={channel} />
          </div>
        )
      },
      columnWidth: "auto",
    },
  ]

  const { headerElements, rows, templateColumns } = useTableColumns({
    columns: tableColumns,
    data: alertNotifications,
  })

  if (fetchUserNotificationSettings.isError) {
    return (
      <Banner type="error">
        {t(langKeys.failed_general_error_try_refreshing_page)}
      </Banner>
    )
  }

  return (
    <GridTable
      header={headerElements}
      rows={rows}
      mobileRows={rows}
      minWidth={0}
      templateColumns={templateColumns}
      disableRowHover
      horizontalScroll
      loading={fetchUserNotificationSettings.isLoading}
    />
  )
}

function LabelWithTooltip({
  label,
  tooltipText,
}: {
  label: string
  tooltipText: string
}) {
  return (
    <TooltipWrapper>
      {label}
      <MTooltip title={tooltipText}>
        <InfoIcon width={20} />
      </MTooltip>
    </TooltipWrapper>
  )
}

const TooltipWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.XS};
`
